import { Injectable } from '@angular/core';
import { Http, Response, Headers, RequestOptions, URLSearchParams } from '@angular/http';
import 'rxjs/add/operator/map';
import { Observable } from 'rxjs/Observable';
import { Configuration } from '../../app.constants';
import { OidcSecurityService } from '../../auth/services/oidc.security.service';
import { UnitSimModel } from '../../shared/models/UnitSimModel';
import { ACTIVE, INACTIVE, CANCELLED } from '../../shared/constants';
import { ProgramControlRequestModel } from '../../shared/models/program-control-related/ProgramControlRequestModel';
import { ProgramControlResultModel } from '../../shared/models/program-control-related/ProgramControlResultModel';
import { UnitScheduledModel } from '../../shared/models/program-control-related/UnitScheduledModel';
import { UnitControlQueueModel } from '../../shared/models/program-control-related/UnitControlQueueModel';
import { UnitScheduledQueueModel } from '../../shared/models/program-control-related/UnitScheduledQueueModel';
import { UnitPingResultModel } from '../../shared/models/program-control-related/UnitPingResultModel';
import { UnitControlCompleteModel } from '../../shared/models/program-control-related/UnitControlCompleteModel';
import { ActivateControlModel } from '../../shared/models/program-control-related/ActivateControlModel';
import { UnitControlModel } from '../../shared/models/program-control-related/UnitControlModel';
import { UnitControlWithStateModel } from '../../shared/models/program-control-related/UnitControlWithStateModel';

@Injectable()
export class UnitProgramControlService {

    private actionUrl: string;
    private headers: Headers;
    private uData: any;

    constructor(private _http: Http, private _configuration: Configuration, private _securityService: OidcSecurityService) {
        this.actionUrl = `${_configuration.Server}/UnitControl/`;
        this._securityService.getUserData().subscribe(data => this.uData = data);
    }

    private setHeaders() {
        this.headers = new Headers();
        this.headers.append('Content-Type', 'application/json');
        this.headers.append('Accept', 'application/json');

        let token = this._securityService.getToken();
        if (token !== '') {
            let tokenValue = 'Bearer ' + token;
            this.headers.append('Authorization', tokenValue);
        }
    }

    public GetUnit = (unitId: string, unitType: string): Observable<UnitSimModel> => {
        this.setHeaders();
        this.setUri(unitType);
        let options = new RequestOptions({ headers: this.headers });
        return this._http.get(this.actionUrl + unitId, options).map(res => res.json());
    }

    // control and programming requests

    public sendCommands = (request: ProgramControlRequestModel): Observable<ProgramControlResultModel> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/ProgramControlUnit`;
        request.UserId = this.uData.sub;
        let options = new RequestOptions({ headers: this.headers });
        return this._http.post(this.actionUrl, JSON.stringify(request), options).map(res => res.json());
    }

    public ping = (request: ProgramControlRequestModel): Observable<UnitPingResultModel> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/Ping`;
        request.UserId = this.uData.sub;
        let options = new RequestOptions({ headers: this.headers });
        return this._http.post(this.actionUrl, JSON.stringify(request), options).map(res => res.json());
    }

    public getControlList = (unitId: string): Observable<UnitControlModel[]> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/GetControlList`;
        let params = new URLSearchParams();
        params.set('unitId', unitId);

        let options = new RequestOptions({ headers: this.headers, params: params });
        return this._http.get(this.actionUrl, options).map(res => res.json());
    }

    public getControlListWithState = (unitId: string): Observable<UnitControlWithStateModel[]> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/GetControlsWithState`;
        let params = new URLSearchParams();
        params.set('unitId', unitId);

        let options = new RequestOptions({ headers: this.headers, params: params });
        return this._http.get(this.actionUrl, options).map(res => res.json());
    }

    public getControlWithState = (controlId: number): Observable<UnitControlWithStateModel> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/GetControlWithState`;
        let params = new URLSearchParams();
        params.set('controlId', controlId.toString());

        let options = new RequestOptions({ headers: this.headers, params: params });
        return this._http.get(this.actionUrl, options).map(res => res.json());
    }

    public getControlComplete = (controlId: number): Observable<UnitControlCompleteModel> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/GetControlComplete`;
        let params = new URLSearchParams();
        params.set('controlId', controlId.toString());

        let options = new RequestOptions({ headers: this.headers, params: params });
        return this._http.get(this.actionUrl, options).map(res => res.json());
    }

    public createControl = (control: UnitControlCompleteModel): Observable<Response> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/AddControl`;
        control.Control.UserId = this.uData.sub;

        let options = new RequestOptions({ headers: this.headers });
        return this._http.post(this.actionUrl, JSON.stringify(control), options).map(res => res.json());
    }

    public updateControl = (control: UnitControlCompleteModel): Observable<Response> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/UpdateControl`;
        control.Control.UserId = this.uData.sub;

        let options = new RequestOptions({ headers: this.headers });
        return this._http.post(this.actionUrl, JSON.stringify(control), options).map(res => res.json());
    }

    public removeControl = (controlId: number): Observable<UnitPingResultModel> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/RemoveControl`;
        let params = new URLSearchParams();
        params.set('controlId', controlId.toString());

        let options = new RequestOptions({ headers: this.headers, params: params });
        return this._http.delete(this.actionUrl, options).map(res => res.json());
    }

    public removeEventCheck = (ecId: number): Observable<Response> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/RemoveEventCheck`;
        let params = new URLSearchParams();
        params.set('eventCheckId', ecId.toString());

        let options = new RequestOptions({ headers: this.headers, params: params });
        return this._http.delete(this.actionUrl, options).map(res => res.json());
    }

    public activateControl = (control: ActivateControlModel): Observable<Response> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/ActivateControl`;

        let options = new RequestOptions({ headers: this.headers });
        return this._http.post(this.actionUrl, JSON.stringify(control), options).map(res => res.json());
    }

    public getControlHistory = (unitId: string, take: number, skip: number): Observable<UnitControlQueueModel[]> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/GetControlHistory`;
        let params = new URLSearchParams();
        params.set('unitId', unitId);
        params.set('take', take.toString());
        params.set('skip', skip.toString());

        let options = new RequestOptions({ headers: this.headers, params: params });
        return this._http.get(this.actionUrl, options).map(res => res.json());
    }

    public getProgrammingHistory = (unitId: string, take: number, skip: number): Observable<UnitControlQueueModel[]> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitControl/GetProgrammingHistory`;
        let params = new URLSearchParams();
        params.set('unitId', unitId);
        params.set('take', take.toString());
        params.set('skip', skip.toString());

        let options = new RequestOptions({ headers: this.headers, params: params });
        return this._http.get(this.actionUrl, options).map(res => res.json());
    }

    // scheduled requests

    public getScheduleds = (unitId: string): Observable<UnitScheduledModel[]> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitScheduled/GetScheduleds`;
        let params = new URLSearchParams();
        params.set('unitId', unitId);
        let options = new RequestOptions({ headers: this.headers, search: params });
        return this._http.get(this.actionUrl, options).map(res => res.json());
    }

    public getScheduledHistory = (unitId: string): Observable<UnitScheduledQueueModel[]> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitScheduled/GetScheduledHistoryByUnit`;
        let params = new URLSearchParams();
        params.set('unitId', unitId);

        let options = new RequestOptions({ headers: this.headers, params: params });
        return this._http.get(this.actionUrl, options).map(res => res.json());
    }

    public updateScheduled = (scheduled: UnitScheduledModel): Observable<Response> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitScheduled/UpdateScheduled`;

        let options = new RequestOptions({ headers: this.headers });
        return this._http.post(this.actionUrl, JSON.stringify(scheduled), options).map(res => res.json());;
    }

    public createScheduled = (scheduled: UnitScheduledModel): Observable<Response> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitScheduled/AddScheduled`;
        scheduled.UserId = this.uData.sub;
        let options = new RequestOptions({ headers: this.headers });
        return this._http.post(this.actionUrl, JSON.stringify(scheduled), options).map(res => res.json());;
    }

    public deleteScheduled = (scheduledId: number): Observable<Response> => {
        this.setHeaders();
        this.actionUrl = `${this._configuration.Server}/UnitScheduled/RemoveScheduled`;
        let params = new URLSearchParams();
        params.set('scheduledId', scheduledId.toString());
        let options = new RequestOptions({ headers: this.headers, search: params });
        return this._http.delete(this.actionUrl, options).map(res => res.json());;
    }

    private setUri(unitType: string): void {
        switch (unitType) {
            case ACTIVE:
                this.actionUrl = `${this._configuration.Server}api/Active/`;
                break;
            case INACTIVE:
                this.actionUrl = `${this._configuration.Server}api/Inactive/`;
                break;
            case CANCELLED:
                this.actionUrl = `${this._configuration.Server}api/Cancelled/`;
                break;
        }
    }
}


// WEBPACK FOOTER //
// ./angularApp/app/units/units-program-control/unit-program-control.service.ts